import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { dashboardAPI } from '@/lib/api';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  LayoutDashboard, Users, Scissors, ShoppingBag, Factory, Truck,
  IndianRupee, Settings, LogOut, ChevronRight, ChevronDown, HardHat,
  CircleDot, FileCheck, CalendarDays, Banknote, Sparkles, Gift,
  BarChart3, Star, Activity, Globe,
} from 'lucide-react';

const NAV_SECTIONS = [
  { to: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard, testId: 'sidebar-dashboard-link' },
  {
    label: 'Fabrics', icon: Scissors, testId: 'sidebar-fabrics-group', key: 'fabrics',
    children: [
      { to: '/app/fabrics', label: 'Catalogue', icon: Scissors, testId: 'sidebar-fabrics-link' },
      { to: '/app/trims', label: 'Trims', icon: CircleDot, testId: 'sidebar-trims-link' },
    ],
  },
  {
    label: 'Clients', icon: Users, testId: 'sidebar-clients-group', key: 'clients',
    children: [
      { to: '/app/clients', label: 'All Clients', icon: Users, testId: 'sidebar-clients-link' },
      { to: '/app/appointments', label: 'Appointments', icon: CalendarDays, testId: 'sidebar-appointments-link', badgeKey: 'today_appointments', badgeColor: 'blue' },
      { to: '/app/clients/referrals', label: 'Referrals', icon: Gift, testId: 'sidebar-referrals-link', roles: ['Admin', 'Sales'] },
      { to: '/app/lookbooks', label: 'Lookbooks', icon: Sparkles, testId: 'sidebar-lookbooks-link', roles: ['Admin', 'Sales'] },
      { to: '/app/events', label: 'Pop-Up Events', icon: Globe, testId: 'sidebar-events-link', roles: ['Admin', 'Sales'] },
    ],
  },
  { to: '/app/orders', label: 'Orders', icon: ShoppingBag, testId: 'sidebar-orders-link', badgeKey: 'active_orders' },
  { to: '/app/alterations', label: 'Alterations', icon: Scissors, testId: 'sidebar-alterations-link', badgeKey: 'active_alterations', badgeColor: 'purple' },
  {
    label: 'Production', icon: Factory, testId: 'sidebar-production-group', key: 'production',
    children: [
      { to: '/app/production', label: 'Workshop', icon: Factory, testId: 'sidebar-production-link' },
      { to: '/app/production/forecasting', label: 'Forecasting', icon: BarChart3, testId: 'sidebar-forecasting-link', roles: ['Admin', 'Production'] },
      { to: '/app/production/workers', label: 'Team', icon: HardHat, testId: 'sidebar-team-link' },
      { to: '/app/production/alteration-patterns', label: 'Alt. Patterns', icon: Activity, testId: 'sidebar-alteration-patterns-link', roles: ['Admin'] },
      { to: '/app/production/worker-reports', label: 'Worker Reports', icon: HardHat, testId: 'sidebar-worker-reports-link', roles: ['Admin'] },
    ],
  },
  {
    label: 'Procurement', icon: Truck, testId: 'sidebar-procurement-group', key: 'procurement',
    badgeKey: 'pending_po_receipts',
    children: [
      { to: '/app/invoices', label: 'Purchase Orders', icon: Truck, testId: 'sidebar-invoices-link' },
      { to: '/app/invoices/vendor', label: 'Vendor Invoices', icon: FileCheck, testId: 'sidebar-vendor-invoices-link' },
      { to: '/app/invoices/vendor-scorecard', label: 'Vendor Scorecard', icon: Star, testId: 'sidebar-vendor-scorecard-link', roles: ['Admin', 'Sales'] },
    ],
  },
  {
    label: 'Analytics', icon: BarChart3, testId: 'sidebar-analytics-group', key: 'analytics',
    roles: ['Admin', 'Sales'],
    children: [
      { to: '/app/analytics', label: 'Overview', icon: BarChart3, testId: 'sidebar-analytics-link' },
      { to: '/app/finances', label: 'Finances', icon: IndianRupee, testId: 'sidebar-finances-link', roles: ['Admin'], badgeKey: 'overdue_balances', badgeColor: 'red' },
      { to: '/app/finances/cash-vouchers', label: 'Cash Vouchers', icon: Banknote, testId: 'sidebar-cash-vouchers-link', badgeKey: 'pending_vouchers', badgeColor: 'orange' },
    ],
  },
  { to: '/app/style-advisor', label: 'Style Advisor', icon: Sparkles, testId: 'sidebar-style-advisor-link', roles: ['Admin', 'Sales'] },
];

const SECONDARY_NAV = [
  { to: '/app/settings', label: 'Settings', icon: Settings, testId: 'sidebar-settings-link', roles: ['Admin'] },
];

const CLIENT_MODE_PATHS = [
  '/app/dashboard', '/app/style-advisor', '/app/clients', '/app/appointments',
  '/app/fabrics', '/app/orders',
];

export function Sidebar({ mobileOpen, onClose, clientMode = false }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [counts, setCounts] = useState({});
  const [expanded, setExpanded] = useState(() => {
    try {
      const saved = sessionStorage.getItem('sidebar_expanded');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  useEffect(() => {
    dashboardAPI.sidebarCounts().then(res => setCounts(res.data || {})).catch(() => {});
    const interval = setInterval(() => {
      dashboardAPI.sidebarCounts().then(res => setCounts(res.data || {})).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Auto-expand group if current path matches a child
  useEffect(() => {
    const path = location.pathname;
    for (const section of NAV_SECTIONS) {
      if (section.children) {
        const match = section.children.some(c => path === c.to || path.startsWith(c.to + '/'));
        if (match && !expanded[section.key]) {
          setExpanded(prev => {
            const next = { ...prev, [section.key]: true };
            sessionStorage.setItem('sidebar_expanded', JSON.stringify(next));
            return next;
          });
        }
      }
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSection = (key) => {
    setExpanded(prev => {
      const next = { ...prev, [key]: !prev[key] };
      sessionStorage.setItem('sidebar_expanded', JSON.stringify(next));
      return next;
    });
  };

  const isAllowed = (item) => {
    if (item.roles && !item.roles.includes(user?.role)) return false;
    if (clientMode && item.to) {
      return CLIENT_MODE_PATHS.some(p => item.to === p || item.to.startsWith(p + '/'));
    }
    return true;
  };

  const renderBadge = (item, prefix) => {
    const val = item.badgeKey ? (counts[item.badgeKey] || 0) : 0;
    if (val <= 0) return null;
    const isRed = item.badgeColor === 'red';
    const bg = isRed ? '#DC2626' : item.badgeColor === 'blue' ? '#2563EB' : item.badgeColor === 'purple' ? '#7C3AED' : item.badgeColor === 'orange' ? '#D97706' : 'var(--brand-navy)';
    return (
      <span className="min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: bg, color: '#fff' }}
        data-testid={`${prefix}sidebar-badge-${item.badgeKey}`}>
        {val}
      </span>
    );
  };

  const renderLeafItem = (item, prefix, indent = false) => {
    const isActive = location.pathname === item.to || (item.to !== '/app/production' && location.pathname.startsWith(item.to + '/'));
    const Icon = item.icon;
    return (
      <NavLink key={item.to} to={item.to} data-testid={`${prefix}${item.testId}`} onClick={onClose}
        className={`group flex items-center gap-3 h-10 ${indent ? 'pl-9 pr-3' : 'px-3'} rounded-lg text-sm font-medium relative
          ${isActive
            ? 'bg-[color:var(--surface-2)] text-[color:var(--navy-ink)] border border-border/70 border-l-[3px] border-l-[color:var(--brand-gold)]'
            : 'text-slate-600 hover:text-[color:var(--navy-ink)] hover:bg-[color:var(--surface-2)] border border-transparent'}`}
        style={{ transition: 'background-color 150ms, color 150ms' }}>
        <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? '' : 'group-hover:translate-x-[1px]'}`} strokeWidth={1.75}
          style={{ transition: 'transform 150ms' }} />
        <span className="flex-1">{item.label}</span>
        {renderBadge(item, prefix)}
        {isActive && !item.badgeKey && <ChevronRight className="w-3.5 h-3.5 ml-auto opacity-40" />}
      </NavLink>
    );
  };

  const renderSection = (section, prefix) => {
    // Simple leaf item (no children)
    if (!section.children) {
      if (!isAllowed(section)) return null;
      return renderLeafItem(section, prefix);
    }

    // Section with children
    if (section.roles && !section.roles.includes(user?.role)) return null;
    const visibleChildren = section.children.filter(isAllowed);
    if (visibleChildren.length === 0) return null;

    const isOpen = !!expanded[section.key];
    const Icon = section.icon;
    const hasActiveChild = visibleChildren.some(c => location.pathname === c.to || location.pathname.startsWith(c.to + '/'));

    return (
      <div key={section.key} data-testid={`${prefix}${section.testId}`}>
        <button onClick={() => toggleSection(section.key)}
          className={`w-full flex items-center gap-3 h-10 px-3 rounded-lg text-sm font-medium transition-colors
            ${hasActiveChild ? 'text-[color:var(--navy-ink)]' : 'text-slate-600 hover:text-[color:var(--navy-ink)] hover:bg-[color:var(--surface-2)]'}`}
          data-testid={`${prefix}toggle-${section.key}`}>
          <Icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.75} />
          <span className="flex-1 text-left">{section.label}</span>
          {renderBadge(section, prefix)}
          {isOpen ? (
            <ChevronDown className="w-3.5 h-3.5 opacity-40 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 opacity-40 flex-shrink-0" />
          )}
        </button>
        {isOpen && (
          <div className="mt-0.5 space-y-0.5">
            {visibleChildren.map(child => renderLeafItem(child, prefix, true))}
          </div>
        )}
      </div>
    );
  };

  const sidebarContent = (prefix = '') => (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="p-4 pb-2">
        <div className="flex items-center gap-3 px-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'var(--brand-navy)' }}>
            <span className="text-sm font-bold" style={{ color: 'var(--brand-gold)', fontFamily: '"Bodoni Moda", Georgia, serif' }}>H</span>
          </div>
          <div>
            <h2 className="text-sm font-semibold leading-tight" style={{ color: 'var(--brand-navy)', fontFamily: '"Bodoni Moda", Georgia, serif' }}>
              Herringbone
            </h2>
            <p className="text-[11px] font-medium" style={{ color: 'var(--brand-gold)' }}>&amp; Sui</p>
          </div>
        </div>
        <div className="mt-4 h-px w-full" style={{ background: 'linear-gradient(90deg, rgba(197,165,90,0.35), rgba(197,165,90,0))' }} />
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3">
        <nav className="space-y-0.5 py-3" data-testid={`${prefix}sidebar-nav`}>
          {NAV_SECTIONS.map(section => renderSection(section, prefix))}
        </nav>

        {!clientMode && (
          <>
            <Separator className="my-2 opacity-60" />
            <nav className="space-y-0.5 py-2">
              {SECONDARY_NAV.filter(isAllowed).map(item => renderLeafItem(item, prefix))}
            </nav>
          </>
        )}
      </ScrollArea>

      {/* User profile */}
      <div className="p-3 mt-auto">
        <Separator className="mb-3 opacity-60" />
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold"
            style={{ backgroundColor: 'rgba(27,42,74,0.08)', color: 'var(--brand-navy)' }}>
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" style={{ color: 'var(--navy-ink)' }}>{user?.name || 'User'}</p>
            <p className="text-[11px] truncate" style={{ color: 'var(--ink-muted)' }}>{user?.role || 'Role'}</p>
          </div>
          <button data-testid={`${prefix}sidebar-logout-button`} onClick={logout}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[rgba(180,35,24,0.06)]"
            style={{ transition: 'background-color 150ms' }} title="Sign out">
            <LogOut className="w-4 h-4" style={{ color: 'var(--danger)' }} />
          </button>
        </div>
        <p className="text-center text-[10px] mt-2 py-2" style={{ color: 'var(--ink-muted)', opacity: 0.7 }}>
          SuiOS v1.0 | Herringbone &amp; Sui
        </p>
      </div>
    </div>
  );

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 xl:hidden" onClick={onClose}
          style={{ transition: 'opacity 220ms' }} />
      )}
      <aside className={`fixed inset-y-0 left-0 z-50 w-[280px] bg-white border-r xl:hidden
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ borderColor: 'var(--border-color)', transition: 'transform 320ms cubic-bezier(0.16,1,0.3,1)' }}>
        {sidebarContent('mobile-')}
      </aside>
      <aside className="hidden xl:flex w-[280px] flex-col bg-white border-r sticky top-0 h-dvh flex-shrink-0"
        style={{ borderColor: 'var(--border-color)' }}>
        {sidebarContent('')}
      </aside>
    </>
  );
}

export default Sidebar;
